import React, { useEffect, useState } from 'react';
import ActionSheet from 'devextreme-react/action-sheet';
import { ActionType } from '../../../borrowers/common/BorrowerConstants';
import { LoadPanel } from 'devextreme-react/load-panel';
import { RefreshEvents } from '../../../common/SharedEvents';
import { handleAdminSuccess, handleAdminError, handleAdminInfo } from '../../../common/SharedFunctions';
import { GeneralContractorActions, getGeneralContractorActions } from '../functions/getGeneralContractorActions';
import { ItemClickEvent } from 'devextreme/ui/action_sheet';
import { LoginApi } from '@apply/api/src/api/loginApi';
import { applicationInsights } from '../../../ApplicationInsights';
import { useGeneralContractorQuery } from '../../GeneralContractorUsersSummary/queries/generalContractorQueries';
import { useGeneralContractorStore } from '../../GeneralContractorUsersSummary/stores/generalContractorStore';

interface GeneralContractorActionsButtonParameters {
    contractorId: number;
}

export const GeneralContractorActionsButton: React.FC<GeneralContractorActionsButtonParameters> = ({ contractorId }) => {
    const loginApi = new LoginApi(applicationInsights);

    const [modalContractorId, set_modalContractorId] = useState<number | undefined>(undefined);
    const { data: generalContractor, isLoading } = useGeneralContractorQuery(modalContractorId);

    const { set_modalContractor } = useGeneralContractorStore();

    const [loadPanelOpen, set_loadPanelOpen] = useState<boolean>(false);
    const [visible, set_visible] = useState<boolean>(false);
    const [target, set_target] = useState<string | undefined>(undefined);
    const [actions, set_actions] = useState<GeneralContractorActions[]>([]);

    useEffect(() => {
        if (!generalContractor) return;

        set_modalContractor(generalContractor);
        set_modalContractorId(undefined);
    }, [generalContractor]);

    const refreshEvents = new RefreshEvents();

    const onActionClicked = (event: React.MouseEvent<HTMLButtonElement>) => {
        const generalContractorActions = getGeneralContractorActions(contractorId);

        if (generalContractorActions.length === 0) {
            handleAdminInfo('No actions available');
            return;
        }

        set_actions(generalContractorActions);
        set_target(`#${(event.target as HTMLButtonElement).id}`);
        set_visible(true);
    };

    const onActionItemClick = async (e: ItemClickEvent) => {
        set_visible(false);

        const { itemData }: { itemData?: GeneralContractorActions } = e;
        if (!itemData) return;

        switch (e.itemData.actionId) {
            case ActionType.SendPasswordResetEmail:
                resetPassword(itemData.contractorId);
                break;
            case ActionType.EditGeneralContractor:
                set_modalContractorId(itemData.contractorId);
                break;
        }
    };

    const resetPassword = async (contractorId: number) => {
        set_loadPanelOpen(true);

        const response = await loginApi.forgotPasswordGeneralContractorId(contractorId);

        if (response.statusCode === 200) {
            handleAdminSuccess('A password reset email has been sent to the selected principal');
            refreshEvents.dispatchRefreshEvent();
        } else {
            handleAdminError(
                new Error('Failed to reset password'),
                'An unexpected error occurred while performing your task. Please try again.'
            );
        }

        set_loadPanelOpen(false);
    };

    return (
        <>
            <button
                id={`generalContractorActionButton_${contractorId}`}
                className='gridButton'
                title='Click to see options'
                onClick={onActionClicked}
            >
                ...
            </button>
            <ActionSheet
                title='Choose action'
                usePopover={true}
                visible={visible}
                target={target}
                items={actions}
                onItemClick={onActionItemClick}
                onVisibleChange={(value) => set_visible(value)}
                showTitle={false}
            />
            <LoadPanel
                hideOnOutsideClick={false}
                visible={isLoading || loadPanelOpen}
                message='Please wait...'
            />
        </>
    );
};
